import Vue from 'vue'

const state = () => ({
  form: null,
  formData: {},
  currentSectionIndex: 0,
});

const getters = {
  form: (state) => state.form,
  formData: (state) => state.formData,
  currentSectionIndex: (state) => state.currentSectionIndex,
  getMappedSubmissionValues: (state) => {
    if (!state.form || !state.form.sections) return [];
    return state.form.sections.flatMap((section) =>
      section.fields.map((field) => ({ field_id: field.id, value: field.value }))
    );
  },
};

const mutations = {
  setForm(state, form) {
    // Check if a different form is being loaded
    const previousFormId = state.form && state.form.id;
    const newFormId = form && form.id;

    // If loading a different form (different ID), clear previous form data
    if (previousFormId && newFormId && previousFormId !== newFormId) {
      // Clear form data and section index
      state.formData = {};
      state.currentSectionIndex = 0;
      // Clean up localStorage for previous form
      localStorage.removeItem(`form_data_${previousFormId}`);
      localStorage.removeItem(`form_section_${previousFormId}`);
    }

    // If removing form (setting to null) or form has no ID, clear data
    if (!form || !newFormId) {
      state.formData = {};
      state.currentSectionIndex = 0;
    }

    state.form = form;
  },
  setFormData(state, data) {
    state.formData = { ...state.formData, ...data };
  },
  clearFormData(state) {
    state.formData = {};
  },
  storeFormValues(state, values) {
    if (!state.form || !state.form.sections) return;
    Object.entries(values).forEach(([key, value]) => {
      const section = state.form.sections.find((sec) => sec.fields.find((f) => f.name === key));
      if (section) {
        const field = section.fields.find((f) => f.name === key);
        if (field) Vue.set(field, 'value', value);
      }
    });
    state.formData = { ...state.formData, ...values };
  },
  setCurrentSection(state, index) {
    state.currentSectionIndex = index;
  },
  clearCurrentSection(state) {
    state.currentSectionIndex = 0;
  },
  clearForm(state) {
    state.form = null;
    state.formData = {};
    state.currentSectionIndex = 0;
  },
};

const actions = {
  saveFormData({ state, commit }, data) {
    commit('setFormData', data);
    if (state.form && state.form.id) {
      localStorage.setItem(`form_data_${state.form.id}`, JSON.stringify(state.formData));
    }
  },
  loadFormData({ state, commit }) {
    if (state.form && state.form.id) {
      const stored = localStorage.getItem(`form_data_${state.form.id}`);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          commit('setFormData', parsed);
          return parsed;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error parsing stored form data:', e);
        }
      }
    }
    return {};
  },
  clearFormData({ state, commit }) {
    commit('clearFormData');
    if (state.form && state.form.id) {
      localStorage.removeItem(`form_data_${state.form.id}`);
    }
  },
  saveCurrentSection({ state, commit }, index) {
    commit('setCurrentSection', index);
    if (state.form && state.form.id) {
      localStorage.setItem(`form_section_${state.form.id}`, String(index));
    }
  },
  loadCurrentSection({ state, commit }) {
    if (state.form && state.form.id) {
      const stored = localStorage.getItem(`form_section_${state.form.id}`);
      const index = stored ? parseInt(stored, 10) || 0 : 0;
      commit('setCurrentSection', index);
      return index;
    }
    return 0;
  },
  clearCurrentSection({ state, commit }) {
    commit('clearCurrentSection');
    if (state.form && state.form.id) {
      localStorage.removeItem(`form_section_${state.form.id}`);
    }
  },
  storeFormValues({ commit }, values) {
    commit('storeFormValues', values);
  },
  clearForm({ state, commit }) {
    // Store form ID before clearing to clean up localStorage
    const formId = state.form && state.form.id;
    commit('clearForm');
    if (formId) {
      localStorage.removeItem(`form_data_${formId}`);
      localStorage.removeItem(`form_section_${formId}`);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
